/**
 * Hex/MvM battle sprites (TexturePacker JSON + PNG) and MvM UI blobs.
 * Character atlases use `animations/mvm-characters/{name}/` on Vercel Blob; other assets may be same-origin `public/`.
 * Fed to the service worker for install-time precache (first visit background fill).
 */
import {
  BASIC_BATTLE_ASSETS_BASE,
  BASIC_BATTLE_ASSETS_URL,
  BATTLE_BG_URL,
  BATTLE_BOTTOM_BAR_LEFT_CORNER_URL,
  BATTLE_BOTTOM_BAR_RIGHT_CORNER_URL,
  BATTLE_BOTTOM_BAR_URL,
  BATTLE_BOTTOM_INFO_LEFT_URL,
  BATTLE_MAP_ASSETS_URLS,
  BATTLE_MAP_ASSETS_URL_MAP3,
  BATTLE_MAP_URL,
  BATTLE_TOP_BAR_CENTER_PLATE_URL,
  BATTLE_TOP_BAR_LEFT_CORNER_URL,
  BATTLE_TOP_BAR_RIGHT_CORNER_URL,
  BATTLE_TOP_BAR_URL,
  BRUXUS_ASSETS_BASE,
  BRUXUS_ATTACK_ASSETS,
  BRUXUS_IDLE_ASSETS,
  BRUXUS_WALK_ASSETS,
  BULA_ASSETS_BASE,
  BULA_ATTACK_ASSETS,
  BULA_IDLE_ASSETS,
  BULA_WALK_ASSETS,
  ARCHERA_ASSETS_BASE,
  ARCHERA_ATTACK_ASSETS,
  ARCHERA_IDLE_ASSETS,
  ARCHERA_WALK_ASSETS,
  KORTHAS_ASSETS_BASE,
  KORTHAS_ATTACK_ASSETS,
  KORTHAS_IDLE_ASSETS,
  KORTHAS_WALK_ASSETS,
  CAELORIA_ASSETS_BASE,
  CAELORIA_ATTACK_ASSETS,
  CAELORIA_IDLE_ASSETS,
  CAELORIA_WALK_ASSETS,
  CHAR_URL,
  CHILD_CHARACTER_ASSETS,
  MVM_CHARACTER_SELECT_MODAL_HEADER_BG_URL,
  MVM_CHARACTER_SELECT_MODAL_BOTTOM_BG_URL,
  MVM_CHARACTER_SELECT_MODAL_BODY_BG_URL,
  MVM_CHARACTER_SELECT_BTN_ATTACK_URL,
  MVM_CHARACTER_SELECT_MODAL_CARD_STATUS_BG_URL,
  HAYATE_ASSETS_BASE,
  HAYATE_ATTACK_ASSETS,
  HAYATE_IDLE_ASSETS,
  HAYATE_WALK_ASSETS,
  BASIC_BATTLE_ASSETS_CACHE_QUERY,
  FINZA_ASSETS_BASE,
  FINZA_ATTACK_ASSETS,
  FINZA_IDLE_ASSETS,
  FINZA_WALK_ASSETS,
  MOTOKO_ASSETS_BASE,
  MOTOKO_ATTACK_ASSETS,
  MOTOKO_IDLE_ASSETS,
  MOTOKO_WALK_ASSETS,
  MVM_ALLIANCE_BATTLE_HUD_ICON_URL,
  MVM_ALLIANCE_BATTLE_PANEL_BG_URL,
  MVM_ALLIANCE_BATTLE_PANEL_BOTTOM_URL,
  MVM_ALLIANCE_BATTLE_PANEL_TOP_URL,
  MVM_ATTACK_NOTI_BG_URL,
  MVM_ELEMENTAL_FIRE_JSON,
  MVM_ELEMENTAL_LIGHTNING_JSON,
  MVM_ELEMENTAL_SHEETS_BASE,
  MVM_ELEMENTAL_WATER_JSON,
  MVM_ELEMENTAL_WOOD_TRAP_URL,
  MVM_END_TURN_BELOW_BADGE_URL,
  MVM_END_TURN_BTN_BG_URL,
  MVM_HUD_ICON_ARMOR_URL,
  MVM_HUD_ICON_ATTACK_URL,
  MVM_HUD_ICON_MOVE_URL,
  MVM_HUD_ICON_POWER_URL,
  MVM_INCOMING_ATTACK_PANEL_BG_URL,
  MVM_INCOMING_ATTACK_PANEL_BOTTOM_URL,
  MVM_INCOMING_ATTACK_PANEL_TOP_URL,
  MVM_SURRENDER_BTN_BG_URL,
  MVM_WORLD_MAP_ATTACK_STATUS_PANEL_BG_URL,
  NYTHERIS_ASSETS_BASE,
  NYTHERIS_ATTACK_ASSETS,
  NYTHERIS_IDLE_ASSETS,
  NYTHERIS_WALK_ASSETS,
  RENA_ASSETS_BASE,
  RENA_ATTACK_ASSETS,
  RENA_IDLE_ASSETS,
  RENA_WALK_ASSETS,
  SAKURA_ASSETS_BASE,
  SAKURA_ATTACK_ASSETS,
  SAKURA_IDLE_ASSETS,
  SAKURA_WALK_ASSETS,
  SOLVARION_ASSETS_BASE,
  SOLVARION_ATTACK_ASSETS,
  SOLVARION_IDLE_ASSETS,
  SOLVARION_WALK_ASSETS,
  VALYRA_ASSETS_BASE,
  VALYRA_ATTACK_ASSETS,
  VALYRA_IDLE_ASSETS,
  VALYRA_WALK_ASSETS,
  VITALIS_ASSETS_BASE,
  VITALIS_ATTACK_ASSETS,
  VITALIS_IDLE_ASSETS,
  VITALIS_WALK_ASSETS,
} from '../pages/hex-battle/constants'
import { MVM_MODAL_PRECACHE_URLS } from '../components/MvMBattle/mvmModalAssets'
import { getAllMvmUiBlobPrecacheUrls } from './mvmBattleUiAssets'

type SpriteBundleEntry = { json: string; png: string; sheetBase?: string }
type SpriteBundle = Record<string, SpriteBundleEntry>

/** `mvmHudBlobUrl` HUD + same-origin bar chrome + elemental VFX atlases (parallel to `getAllMvmUiBlobPrecacheUrls`). */
const MV_M_HEX_EXTRA_PRELOAD: readonly string[] = [
  BATTLE_TOP_BAR_URL,
  BATTLE_TOP_BAR_CENTER_PLATE_URL,
  MVM_SURRENDER_BTN_BG_URL,
  BATTLE_BOTTOM_BAR_URL,
  BATTLE_BOTTOM_INFO_LEFT_URL,
  MVM_END_TURN_BTN_BG_URL,
  MVM_END_TURN_BELOW_BADGE_URL,
  MVM_ATTACK_NOTI_BG_URL,
  MVM_INCOMING_ATTACK_PANEL_BG_URL,
  MVM_INCOMING_ATTACK_PANEL_TOP_URL,
  MVM_INCOMING_ATTACK_PANEL_BOTTOM_URL,
  ...MVM_MODAL_PRECACHE_URLS,
  BATTLE_TOP_BAR_LEFT_CORNER_URL,
  BATTLE_TOP_BAR_RIGHT_CORNER_URL,
  BATTLE_BOTTOM_BAR_LEFT_CORNER_URL,
  BATTLE_BOTTOM_BAR_RIGHT_CORNER_URL,
  MVM_WORLD_MAP_ATTACK_STATUS_PANEL_BG_URL,
  MVM_ALLIANCE_BATTLE_PANEL_BG_URL,
  MVM_ALLIANCE_BATTLE_PANEL_TOP_URL,
  MVM_ALLIANCE_BATTLE_PANEL_BOTTOM_URL,
  MVM_ALLIANCE_BATTLE_HUD_ICON_URL,
  MVM_HUD_ICON_ATTACK_URL,
  MVM_HUD_ICON_ARMOR_URL,
  MVM_HUD_ICON_MOVE_URL,
  MVM_HUD_ICON_POWER_URL,
]

const ELEMENTAL_BATTLE_ATLAS_URLS: readonly string[] = (() => {
  const root = MVM_ELEMENTAL_SHEETS_BASE.replace(/\/$/, '')
  return [
    MVM_ELEMENTAL_FIRE_JSON,
    `${root}/fire.png`,
    MVM_ELEMENTAL_WATER_JSON,
    `${root}/water.png`,
    MVM_ELEMENTAL_LIGHTNING_JSON,
    `${root}/lightning.png`,
    MVM_ELEMENTAL_WOOD_TRAP_URL,
  ]
})()

function addCharacterSprites(out: Set<string>, base: string, idle: SpriteBundle, walk: SpriteBundle, attack: SpriteBundle) {
  const defRoot = base.replace(/\/$/, '')
  for (const bundle of [idle, walk, attack]) {
    for (const entry of Object.values(bundle)) {
      out.add(entry.json)
      const pngRoot = (entry.sheetBase ?? defRoot).replace(/\/$/, '')
      out.add(`${pngRoot}/${entry.png}`)
    }
  }
}

/** Stable list for SW postMessage + debugging. */
export function getMvmHexBattlePrecacheUrls(): string[] {
  const out = new Set<string>()

  addCharacterSprites(out, SAKURA_ASSETS_BASE, SAKURA_IDLE_ASSETS, SAKURA_WALK_ASSETS, SAKURA_ATTACK_ASSETS)
  addCharacterSprites(out, RENA_ASSETS_BASE, RENA_IDLE_ASSETS, RENA_WALK_ASSETS, RENA_ATTACK_ASSETS)
  addCharacterSprites(out, HAYATE_ASSETS_BASE, HAYATE_IDLE_ASSETS, HAYATE_WALK_ASSETS, HAYATE_ATTACK_ASSETS)
  addCharacterSprites(out, SOLVARION_ASSETS_BASE, SOLVARION_IDLE_ASSETS, SOLVARION_WALK_ASSETS, SOLVARION_ATTACK_ASSETS)
  addCharacterSprites(out, MOTOKO_ASSETS_BASE, MOTOKO_IDLE_ASSETS, MOTOKO_WALK_ASSETS, MOTOKO_ATTACK_ASSETS)
  addCharacterSprites(out, VALYRA_ASSETS_BASE, VALYRA_IDLE_ASSETS, VALYRA_WALK_ASSETS, VALYRA_ATTACK_ASSETS)
  addCharacterSprites(out, BRUXUS_ASSETS_BASE, BRUXUS_IDLE_ASSETS, BRUXUS_WALK_ASSETS, BRUXUS_ATTACK_ASSETS)
  addCharacterSprites(out, FINZA_ASSETS_BASE, FINZA_IDLE_ASSETS, FINZA_WALK_ASSETS, FINZA_ATTACK_ASSETS)
  addCharacterSprites(out, VITALIS_ASSETS_BASE, VITALIS_IDLE_ASSETS, VITALIS_WALK_ASSETS, VITALIS_ATTACK_ASSETS)
  addCharacterSprites(out, BULA_ASSETS_BASE, BULA_IDLE_ASSETS, BULA_WALK_ASSETS, BULA_ATTACK_ASSETS)
  addCharacterSprites(out, CAELORIA_ASSETS_BASE, CAELORIA_IDLE_ASSETS, CAELORIA_WALK_ASSETS, CAELORIA_ATTACK_ASSETS)
  addCharacterSprites(out, NYTHERIS_ASSETS_BASE, NYTHERIS_IDLE_ASSETS, NYTHERIS_WALK_ASSETS, NYTHERIS_ATTACK_ASSETS)
  addCharacterSprites(out, ARCHERA_ASSETS_BASE, ARCHERA_IDLE_ASSETS, ARCHERA_WALK_ASSETS, ARCHERA_ATTACK_ASSETS)
  addCharacterSprites(out, KORTHAS_ASSETS_BASE, KORTHAS_IDLE_ASSETS, KORTHAS_WALK_ASSETS, KORTHAS_ATTACK_ASSETS)

  // Child characters (20) — same Blob layout, registered via the generated CHILD_CHARACTER_ASSETS registry.
  for (const set of Object.values(CHILD_CHARACTER_ASSETS)) {
    addCharacterSprites(out, set.base, set.idle, set.walk, set.attack)
  }

  out.add(CHAR_URL)
  out.add(MVM_CHARACTER_SELECT_MODAL_HEADER_BG_URL)
  out.add(MVM_CHARACTER_SELECT_MODAL_BOTTOM_BG_URL)
  out.add(MVM_CHARACTER_SELECT_MODAL_BODY_BG_URL)
  out.add(MVM_CHARACTER_SELECT_BTN_ATTACK_URL)
  out.add(MVM_CHARACTER_SELECT_MODAL_CARD_STATUS_BG_URL)
  out.add(BATTLE_BG_URL)
  for (const u of BATTLE_MAP_ASSETS_URLS) out.add(u)
  out.add(BATTLE_MAP_ASSETS_URL_MAP3)
  const battleBase = BASIC_BATTLE_ASSETS_BASE.replace(/\/$/, '')
  out.add(`${battleBase}/battle-map-assets-3.png`)

  out.add(BATTLE_MAP_URL)
  out.add(BASIC_BATTLE_ASSETS_URL)
  out.add(`${battleBase}/basic-battle-assets.png${BASIC_BATTLE_ASSETS_CACHE_QUERY}`)
  out.add(`${battleBase}/battle-map-assets.png`)
  out.add(`${battleBase}/battle-map-assets-2.png`)

  for (const u of ELEMENTAL_BATTLE_ATLAS_URLS) out.add(u)
  for (const u of MV_M_HEX_EXTRA_PRELOAD) out.add(u)

  for (const u of getAllMvmUiBlobPrecacheUrls()) out.add(u)

  return [...out].filter(Boolean)
}

/** Battle PNG/WebP/JPEG URLs from {@link getMvmHexBattlePrecacheUrls} — for Pixi GPU warm. */
export function getMvmHexBattleRasterWarmUrls(): string[] {
  const re = /\.(png|webp|jpe?g)(\?|$)/i
  return [...new Set(getMvmHexBattlePrecacheUrls().filter((u) => re.test(u)))]
}
